import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Phone, Wallet } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { PhoneWithOperator } from '@/components/ui/phone-with-operator';
import { useCoberturaClaroByCep } from '@/hooks/useParqueVivoByCnpj';
import { usePlanosCpfOferta } from '@/hooks/usePlanosCpfOferta';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';
import {
  Bloco, BlocoOferta, BlocoOndeMora, BlocoPorQueLigar, BlocoQuemE,
  GRID, Par, ROTULO, Tile, cpfFmt, moeda, telefoneFmt, type Oferta,
} from './lead-cpf-blocos';

interface Props {
  lead: LeadCarteira;
  onLigar?: (telefone: string) => void;
  onLevarParaProposta?: (ofertas: Oferta[]) => void;
}

const digitos = (t: unknown) => String(t ?? '').replace(/\D/g, '');
const mesmoNumero = (a: unknown, b: unknown) => {
  const x = digitos(a).slice(-8);
  return x.length === 8 && x === digitos(b).slice(-8);
};

// O mailing TT de onde o lead de renovação nasceu (lead_origem_id) traz o que o
// lead CPF não copiou: sócios e o telefone de cada um, complemento, data da
// fatura e o status da planilha. Só leitura.
function useMailingDeOrigem(leadId: string, origemId: string | null | undefined) {
  return useQuery({
    queryKey: ['lead-renov-cpf-origem', leadId, origemId ?? null],
    enabled: !!leadId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Record<string, unknown>> => {
      let id = origemId ?? null;
      if (!id) {
        const { data } = await supabase.from('leads_carteira').select('lead_origem_id').eq('id', leadId).maybeSingle();
        id = ((data as { lead_origem_id?: string | null } | null)?.lead_origem_id) ?? null;
      }
      if (!id) return {};
      const { data } = await supabase.from('leads_carteira').select('dados_discadora').eq('id', id).maybeSingle();
      return ((data as { dados_discadora?: Record<string, unknown> | null } | null)?.dados_discadora) ?? {};
    },
  });
}

// 2 · Como falar — na renovação não existe gestor Vivo: quem atende é o contato
// que o mailing confirmou, o titular pelas linhas da conta dele, ou um sócio da
// empresa onde as linhas são usadas. Cada número diz de quem é, para o disparo
// cair na pessoa certa (Douglas 28/09/2026).
function BlocoComoFalarRenovacao({ lead, linhasBase, socios, onLigar }: {
  lead: LeadCarteira;
  linhasBase: string[];
  socios: { nome: string; telefone: string | null }[];
  onLigar?: (t: string) => void;
}) {
  const contatoNome = lead.contato_real_nome || null;
  const contatoTel = digitos(lead.contato_real_telefone);
  const titular = lead.nome_proprietario || lead.razao_social || null;
  const contatoELinha = !!contatoTel && linhasBase.some((l) => mesmoNumero(l, contatoTel));
  const contatoNaoETitular = !!contatoNome && !!titular && contatoNome.trim().toUpperCase() !== titular.trim().toUpperCase();
  const outros = [lead.telefone, lead.telefone_2, lead.telefone_3, lead.telefone_4, lead.telefone_adm]
    .map(digitos)
    .filter((t, i, arr) => t.length >= 10 && arr.indexOf(t) === i)
    .filter((t) => !mesmoNumero(t, contatoTel) && !linhasBase.some((l) => mesmoNumero(l, t)));
  const botao = (t: string) => (
    <span key={t} className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => onLigar?.(t)}
        className="rounded-lg border border-border px-2.5 py-1.5 text-[12.5px] font-semibold tabular-nums hover:border-primary hover:text-primary"
      >
        {telefoneFmt(t)}
      </button>
      <PhoneWithOperator phone={t} variant="badge-only" />
    </span>
  );
  return (
    <Bloco
      titulo="Como falar"
      meta={`${contatoNome ? 'contato confirmado + ' : ''}${linhasBase.length} linha${linhasBase.length === 1 ? '' : 's'} da conta`}
      icone={<Phone className="h-3 w-3" />}
    >
      {contatoNome && contatoTel ? (
        <div className="flex flex-col gap-1.5 rounded-xl border border-white/[0.07] bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] p-3.5 text-white">
          <span className="text-[9.5px] font-bold uppercase tracking-wider text-white/70">
            Contato confirmado no mailing{contatoELinha ? ' · é uma das linhas da conta' : ''}
          </span>
          <span className="text-[17px] font-bold leading-tight">{contatoNome}</span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onLigar?.(contatoTel)}
              className="rounded-lg bg-white/[0.12] px-2.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-white/[0.2]"
            >
              {telefoneFmt(contatoTel)}
            </button>
          </div>
          {contatoNaoETitular ? (
            <span className="text-[11px] text-white/70">Não é o titular: a conta está no nome de {titular}.</span>
          ) : null}
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground">
          O mailing não confirmou um contato. Fale com o titular pelas linhas da conta.
        </p>
      )}
      {linhasBase.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <span className={ROTULO}>Linhas da conta · do titular</span>
          <div className="flex flex-wrap gap-2">{linhasBase.map(digitos).map(botao)}</div>
        </div>
      ) : null}
      {outros.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <span className={ROTULO}>Outros telefones do lead</span>
          <div className="flex flex-wrap gap-2">{outros.map(botao)}</div>
        </div>
      ) : null}
      {socios.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <span className={ROTULO}>Sócios da empresa onde as linhas são usadas</span>
          <div className="flex flex-col gap-1">
            {socios.map((so) => (
              <span key={so.nome} className="flex flex-wrap items-center gap-2 text-[12.5px]">
                <b className="font-semibold">{so.nome}</b>
                {so.telefone ? botao(so.telefone) : <span className="text-muted-foreground">sem telefone no mailing</span>}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {lead.email ? <Par rotulo="E-mail do lead" valor={lead.email} title={lead.email} /> : null}
    </Bloco>
  );
}

// Renovação CPF: cliente da Claro (base TT). Reduzir o que ele paga e entrar com
// produto novo. Mesmo esqueleto da Portabilidade CPF — muda o conteúdo.
export function LeadDados360RenovacaoCpf({ lead, onLigar, onLevarParaProposta }: Props) {
  const master = (lead.dados_master_lead || {}) as Record<string, unknown>;
  const perfil = String(master.perfil || '').toUpperCase();
  const valorMovel = master.valor_movel as number | undefined;
  const valorCombo = master.valor_combo as number | undefined;
  const valorResidencial = master.valor_residencial as number | undefined;
  const linhasBase = Array.isArray(master.linhas_base) ? (master.linhas_base as string[]) : [];
  const cep = (master.cep as string) || null;
  // O mailing grava "SIM" ou o próprio endereço novo neste campo.
  const mudouCru = String(master.mudou_endereco || '').trim();
  const mudouEndereco = !!mudouCru && !/^(n[aã]o|nao)$/i.test(mudouCru);
  const enderecoNovo = (master.endereco_novo as string) || (mudouEndereco && !/^sim$/i.test(mudouCru) ? mudouCru : null);
  const { data: origem = {} } = useMailingDeOrigem(lead.id, (lead as { lead_origem_id?: string | null }).lead_origem_id);
  const socios = String(origem.socios || '').split(';').map((n) => n.trim()).filter(Boolean)
    .map((nome, i) => ({ nome, telefone: digitos(i === 0 ? origem.telefone1_socio : i === 1 ? origem.telefone2_socio : null) || null }));
  const enderecoCompleto = [
    [lead.endereco_empresa, master.numero_endereco || origem.numero_endereco].filter(Boolean).join(', '),
    origem.complemento, master.bairro || origem.bairro,
  ].filter(Boolean).join(' · ') || null;
  const coberturaBase = String(master.cobertura_base || lead.cobertura_fibra || '').toUpperCase().startsWith('S');

  const { data: catalogo } = usePlanosCpfOferta();
  const { data: coberturaClaro } = useCoberturaClaroByCep(cep);
  const temFibraNossa = coberturaClaro?.coberto === true;
  const temMovel = perfil === 'COMBO' || (valorMovel ?? 0) > 0 || linhasBase.length > 0;
  const temFixa = (valorResidencial ?? 0) > 0 || perfil === 'RESIDENCIAL';

  // Cada oferta só entra quando o motivo é verdadeiro. Sem motivo, não aparece —
  // isto é a aba Dados, não a proposta.
  const ofertas = useMemo<Oferta[]>(() => {
    if (!catalogo) return [];
    const lista: Oferta[] = [];
    if (!temFixa && (temFibraNossa || coberturaBase) && catalogo.fibra) {
      lista.push({
        nome: catalogo.fibra.nome, valor: catalogo.fibra.valor, destaque: true,
        selo: mudouEndereco ? 'Mudou de endereço' : 'Tem cobertura no CEP',
        porque: mudouEndereco
          ? 'Endereço novo na base: instalação nova é a melhor hora de vender internet fixa.'
          : 'Não tem internet fixa com a gente e o endereço é atendido.',
      });
    }
    if (catalogo.tv) {
      lista.push({
        nome: catalogo.tv.nome, valor: catalogo.tv.valor,
        selo: 'Não tem TV na conta',
        porque: 'Fecha junto com a fibra e sustenta o ticket.',
      });
    }
    if (temMovel && catalogo.dependente) {
      lista.push({
        nome: catalogo.dependente.nome, valor: catalogo.dependente.valor,
        selo: 'Sem pacote próprio',
        porque: 'Divide o pacote do titular — linha a mais sem trocar o plano dele.',
      });
    }
    if (temMovel && !temFixa && catalogo.convergente) {
      lista.push({
        nome: catalogo.convergente.nome, valor: catalogo.convergente.valor,
        selo: 'No lugar da fibra', alternativa: true,
        porque: 'Fibra e móvel no mesmo pacote. Entra NO LUGAR da fibra avulsa, por isso não soma no total.',
      });
    }
    return lista;
  }, [catalogo, temFixa, temMovel, temFibraNossa, coberturaBase, mudouEndereco]);

  const contaHoje = (valorMovel ?? 0) + (valorResidencial ?? 0) || (lead.valor_fatura_atual ?? 0);
  const somaOfertas = ofertas.filter((o) => !o.alternativa).reduce((s, o) => s + o.valor, 0);

  return (
    <div className="space-y-3">
      <BlocoPorQueLigar
        meta={perfil === 'COMBO' ? 'tem móvel e fixa na Claro' : 'só fixa na Claro'}
        frase={
          <>
            {contaHoje > 0
              ? <>Ele paga <b>{moeda(contaHoje)}</b> por mês na Claro</>
              : <>Ele é cliente <b>{temFixa ? 'de fixa' : ''}</b> na Claro — a base não trouxe o valor da conta</>}
            {temMovel && linhasBase.length > 0 ? (
              <> em <b>{linhasBase.length} linha{linhasBase.length > 1 ? 's' : ''}</b></>
            ) : null}
            {temFibraNossa || coberturaBase ? <>, e o endereço dele <b>tem fibra</b></> : null}
            {temMovel
              ? <>. Dá para baixar o móvel ao uso real e pôr produto novo no lugar.</>
              : <>. Ele não tem linha móvel com a gente — é aí que entra a venda.</>}
          </>
        }
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
          <Par rotulo="Perfil na base" valor={perfil === 'COMBO' ? 'Combo' : perfil === 'RESIDENCIAL' ? 'Residencial' : '—'} />
          <Par rotulo="Móvel" valor={(valorMovel ?? 0) > 0 ? moeda(valorMovel) : temMovel ? 'tem — valor não veio' : '—'} />
          <Par rotulo="Residencial" valor={(valorResidencial ?? 0) > 0 ? moeda(valorResidencial) : temFixa ? 'tem — valor não veio' : '—'} />
          {(valorCombo ?? 0) > 0 ? <Par rotulo="Combo na base" valor={moeda(valorCombo)} /> : null}
          <Par rotulo="Operadora hoje" valor={lead.operadora_atual || 'Claro'} />
          {origem.data_fatura ? <Par rotulo="Fatura do mailing" valor={String(origem.data_fatura)} /> : null}
        </div>
      </BlocoPorQueLigar>

      <BlocoComoFalarRenovacao lead={lead} linhasBase={linhasBase} socios={socios} onLigar={onLigar} />

      <BlocoOferta ofertas={ofertas} onLevarParaProposta={onLevarParaProposta} />

      <div className={GRID}>
        <Bloco titulo="A conta dele" meta="simulação · nada é gravado" icone={<Wallet className="h-3 w-3" />}>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
            <div className="flex flex-col gap-1.5">
              <span className={ROTULO}>Hoje paga</span>
              {(valorMovel ?? 0) > 0 ? <span className="text-[13px]">Móvel — <b>{moeda(valorMovel)}</b></span> : null}
              {(valorResidencial ?? 0) > 0 ? <span className="text-[13px]">Residencial — <b>{moeda(valorResidencial)}</b></span> : null}
              {contaHoje > 0
              ? <Tile rotulo="Total hoje" valor={moeda(contaHoje)} />
              : <span className="text-[12px] text-muted-foreground">A base não trouxe valor. Confirme na ligação o que ele paga hoje.</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <span className={ROTULO}>Se ele aceitar tudo</span>
              {ofertas.filter((o) => !o.alternativa).map((o) => (
                <span key={o.nome} className="text-[13px]">{o.nome} — <b>{moeda(o.valor)}</b></span>
              ))}
              {ofertas.filter((o) => o.alternativa).map((o) => (
                <span key={o.nome} className="text-[13px] text-muted-foreground">
                  ou {o.nome} — <b>{moeda(o.valor)}</b> (no lugar da fibra)
                </span>
              ))}
              <Tile rotulo="Entra de novo" valor={moeda(somaOfertas)} />
            </div>
          </div>
          <p className="text-[12px] text-muted-foreground">
            Móvel, combo e residencial vêm separados da base, como a planilha mandou — nunca somados entre si. Plano e
            GB só com a fatura, que fica na aba Fatura.
          </p>
        </Bloco>

        <BlocoOndeMora
          endereco={enderecoCompleto}
          cidade={(master.cidade as string) || null}
          uf={lead.uf}
          cep={cep}
          coberturaBase={coberturaBase}
          coberturaNossa={coberturaClaro == null ? null : temFibraNossa}
          enderecoNovo={mudouEndereco ? (enderecoNovo || 'Sim') : null}
          fonte="vem do mailing"
        />
      </div>

      <BlocoQuemE meta={perfil === 'COMBO' ? 'tem móvel e fixa' : 'só fixa'}>
        <Par rotulo="Titular" valor={lead.nome_proprietario || 'a confirmar'} />
        <Par rotulo="CPF" valor={cpfFmt(lead.cpf)} />
        <Par
          rotulo="Empresa onde a linha é usada"
          valor={(master.empresa_da_linha as string) || lead.razao_social}
          title={(master.empresa_da_linha as string) || lead.razao_social}
        />
        <Par
          rotulo="Linhas conhecidas"
          valor={linhasBase.length > 0
            ? linhasBase.map(telefoneFmt).join(' · ')
            : temFixa ? 'nenhuma linha móvel na base — só fixa' : 'a base não trouxe as linhas'}
        />
      </BlocoQuemE>
    </div>
  );
}
